// USGA / Golf Genius card-off — the shared tiebreak for equal qualifying
// totals, used by BOTH seeding spots (the flight-build comparator in data.js
// and the template/104-team position sort in aiActionExecutor.js) so ties
// break the SAME way everywhere. Pure — no firebase / react — so it is
// unit-testable headlessly.

// Normalize a holes value to a 1-indexed { 1..18: strokes } map. Handles the
// three shapes seen in the data:
//   • object { "1": s, … "18": s }               (native/web qualify submit)
//   • array  [null, h1..h18]  (length 19)         (Import GG PDF / qualify node)
//   • array  [h1..h18]        (length 18)         (0-indexed, legacy)
// The leading-null array is detected so hole N maps to the correct stroke —
// getting this right is essential for the USGA segment sums below (a one-hole
// shift would sum holes 9–17 instead of the back 9, 10–18).
export function holesByNumber(holes) {
  const out = {}
  if (!holes) return out
  if (Array.isArray(holes)) {
    const oneIndexed = holes.length >= 19 && (holes[0] == null || !Number.isFinite(Number(holes[0])))
    holes.forEach((v, i) => {
      const hole = oneIndexed ? i : i + 1
      if (hole >= 1 && hole <= 18) out[hole] = Number(v)
    })
    return out
  }
  for (const [k, v] of Object.entries(holes)) {
    const n = Number(k)
    if (Number.isFinite(n)) out[n] = Number(v)
  }
  return out
}

// Standard USGA card-off (how Golf Genius seeds equal totals): compare the
// score for the BACK 9 (holes 10–18); if tied, the last 6 (13–18); if tied,
// the last 3 (16–18); if tied, hole 18. The FIRST segment whose totals differ
// decides — the lower total seeds higher. A segment with any missing hole is
// skipped (with full cards this never happens).
//
// Returns  <0  if A seeds ahead (lower strokes),
//          >0  if B seeds ahead,
//           0  if still perfectly tied after all four segments.
export function usgaCardOff(holesA, holesB) {
  const ha = holesByNumber(holesA)
  const hb = holesByNumber(holesB)
  const sumRange = (h, lo, hi) => {
    let total = 0
    for (let n = lo; n <= hi; n++) {
      const v = Number(h[n])
      if (!Number.isFinite(v)) return null // incomplete card for this segment
      total += v
    }
    return total
  }
  for (const seg of CARDOFF_SEGMENTS) {
    const sa = sumRange(ha, seg.lo, seg.hi)
    const sb = sumRange(hb, seg.lo, seg.hi)
    if (sa == null || sb == null) continue
    if (sa !== sb) return sa - sb
  }
  return 0
}

// The four USGA card-off segments, in priority order, with display labels.
export const CARDOFF_SEGMENTS = [
  { key: 'back9', label: 'back-9', lo: 10, hi: 18 },
  { key: 'last6', label: 'last-6', lo: 13, hi: 18 },
  { key: 'last3', label: 'last-3', lo: 16, hi: 18 },
  { key: 'hole18', label: 'hole 18', lo: 18, hi: 18 },
]

// Sum of a hole range (inclusive). Null if any hole in the range is missing.
export function segmentSum(holes, lo, hi) {
  const h = holesByNumber(holes)
  let total = 0
  for (let n = lo; n <= hi; n++) {
    const v = Number(h[n])
    if (!Number.isFinite(v)) return null
    total += v
  }
  return total
}

// For a team tied on total with others, return { key, label, value } for the
// FIRST card-off segment on which the tied group is NOT unanimous — i.e. the
// segment that actually decides this team's seed within the tie — using THIS
// team's value for it. Returns null for a lone team or a group tied all the way
// through (perfect tie). `groupHolesList` includes this team's own holes.
export function decidingTiebreak(myHoles, groupHolesList) {
  if (!Array.isArray(groupHolesList) || groupHolesList.length < 2) return null
  for (const seg of CARDOFF_SEGMENTS) {
    const sums = groupHolesList.map((h) => segmentSum(h, seg.lo, seg.hi))
    if (sums.some((s) => s == null)) continue
    if (new Set(sums).size > 1) {
      return { key: seg.key, label: seg.label, value: segmentSum(myHoles, seg.lo, seg.hi) }
    }
  }
  return null
}
